import type { ReactElement } from 'react'

import { SITE_CONFIG } from '@maria-cavalheiro-website/shared-types/config'
import { PageShell, type NavigationItem } from '@maria-cavalheiro-website/shared-ui-web'

import { AboutPage } from '../features/pages/about/about-page.tsx'
import { HomePage } from '../features/pages/home/home-page.tsx'
import { NotFoundPage } from '../features/pages/not-found/not-found-page.tsx'
import { PoemPage } from '../features/pages/poem/poem-page.tsx'
import { useActiveSection } from '../features/navigation/use-active-section.ts'
import { useNavigationMenu } from '../features/navigation/use-navigation-menu.ts'
import { listPoems } from '../features/poems/poem-collection.ts'
import { ThemeToggle } from '../features/theme/theme-toggle.tsx'
import { useTheme, type ResolvedTheme } from '../features/theme/use-theme.ts'
import { ABOUT_PATH, HOME_PATH, type Route } from './routes.ts'

/**
 * The three home sections, in document order. The order matters twice: it is the
 * order they appear in the nav, and it is the order scroll-spy resolves ties in.
 *
 * `sobre` LEFT THIS LIST when the biography became `/sobre`. Leaving it would have
 * had the observer look for an element that no document contains.
 */
export const SECTION_IDS = ['inicio', 'poemas', 'social'] as const

type SectionId = (typeof SECTION_IDS)[number]

const SECTION_LABELS: Record<SectionId, string> = {
  inicio: 'Início',
  poemas: 'Poemas',
  social: 'Compartilhar',
}

const ABOUT_LABEL = 'Sobre mim'

/** The biography route's `h1`, which also names its `<article>`. */
const ABOUT_HEADING_ID = 'titulo-sobre'

const SKIP_LINK_LABEL = 'Ir para o conteúdo principal'
const NAVIGATION_LABEL = 'Seções do site'
/**
 * Names the region, not the direction — `aria-expanded` carries the direction and
 * a label that repeated it would have a screen reader say the state twice.
 */
const NAVIGATION_TOGGLE_LABEL = 'Menu'
const BRAND_SUFFIX = 'poemas'

/**
 * Fixed, not `new Date().getFullYear()`.
 *
 * A clock in the render path makes the prerendered HTML non-reproducible and
 * would silently drift away from the committed snapshot at midnight on 31
 * December. Phase 0012 diffs two consecutive builds and would catch it.
 */
const COPYRIGHT_YEAR = '2026'
const FOOTER_NOTE = `© ${COPYRIGHT_YEAR} ${SITE_CONFIG.author} · Todos os poemas são de autoria própria`
const FOOTER_DOMAIN = new URL(SITE_CONFIG.origin).host

const TWEMOJI_URL = 'https://github.com/jdecked/twemoji'
const CC_BY_URL = 'https://creativecommons.org/licenses/by/4.0/'

/**
 * The attribution CC-BY 4.0 requires for the poem icons.
 *
 * Not decoration and not optional: the emblem beside every poem is composed from
 * vendored Twemoji artwork, and the licence it arrives under asks for the
 * creator, the licence, a link to each, and a statement of whether the material
 * was modified. All four are here, in the reader's language, and
 * `twemoji-shapes.test.ts` is what keeps "sem modificações" true.
 *
 * `rel="noopener"` on both: `noreferrer` is deliberately absent, because an
 * attribution link that hides where it came from defeats its own purpose.
 */
const FOOTER_CREDIT = (
  <>
    {'Ilustrações dos poemas por '}
    <a href={TWEMOJI_URL} rel="noopener" target="_blank">
      Twemoji
    </a>
    {', sem modificações · licença '}
    <a href={CC_BY_URL} rel="noopener" target="_blank">
      CC-BY 4.0
    </a>
  </>
)

/**
 * The three section anchors only exist on the home document.
 *
 * The design side-stepped this by being a single document; we cannot. On any other
 * route the same items become absolute forms (`/#poemas`) that navigate home and
 * land on the right section — rather than dead links.
 *
 * NAV ORDER IS NOT SECTION ORDER. "Sobre mim" sits second, where its section used
 * to, but it is a route now: one href at every viewport and on every document, and
 * `current` rather than a scroll-spy match is what marks it.
 */
function buildNavigation(route: Route['kind']): readonly NavigationItem[] {
  const anchor = (sectionId: SectionId): NavigationItem => ({
    href: `${route === 'home' ? '' : HOME_PATH}#${sectionId}`,
    label: SECTION_LABELS[sectionId],
    sectionId,
  })

  return [
    anchor('inicio'),
    { href: ABOUT_PATH, label: ABOUT_LABEL, current: route === 'about' },
    anchor('poemas'),
    anchor('social'),
  ]
}

/**
 * Three frozen lists rather than one built per render. The nav is identical for
 * every poem and for the 404, so `poem` and `not-found` share one.
 */
const NAVIGATION_BY_ROUTE: Record<Route['kind'], readonly NavigationItem[]> = {
  home: buildNavigation('home'),
  about: buildNavigation('about'),
  poem: buildNavigation('poem'),
  'not-found': buildNavigation('not-found'),
}

export interface AppProps {
  readonly route: Route
}

interface RouteViewProps extends AppProps {
  readonly theme: ResolvedTheme
}

/** Total route -> page mapping. Every arm is reachable and independently tested. */
function RouteView({ route, theme }: RouteViewProps): ReactElement {
  if (route.kind === 'home') {
    return <HomePage poems={listPoems()} theme={theme} />
  }

  if (route.kind === 'about') {
    return <AboutPage headingId={ABOUT_HEADING_ID} />
  }

  if (route.kind === 'poem') {
    return <PoemPage poem={route.poem} />
  }

  return <NotFoundPage />
}

/**
 * Application root and the only container in the shell.
 *
 * RENDER is deterministic — no clocks, no randomness, no environment probing — so
 * the prerendered HTML and the hydrated tree are byte-identical. Probing does
 * happen, but only inside effects, after mount: `useTheme` reads storage and the
 * OS colour-scheme preference, `useActiveSection` observes scroll position, and
 * the hero sky reads `new Date()`. All three start from a value the server also
 * produces, so none of them can cause a hydration mismatch.
 */
export function App({ route }: AppProps): ReactElement {
  const isHome = route.kind === 'home'

  // Called ONCE, here. The toggle in the header and the sky in the hero must
  // agree on the resolved theme, and two independent hook instances would not:
  // the second would never learn that the first had flipped the attribute.
  const { resolved, toggle } = useTheme()

  // Only the home document has sections to spy on. Passing an empty list keeps
  // the hook call unconditional (rules of hooks) while doing no observer work.
  const activeSectionId = useActiveSection(isHome ? SECTION_IDS : [])

  // The mobile navigation disclosure. Collapsed on every first render, server and
  // client alike, which is what keeps the two byte-identical.
  const menu = useNavigationMenu()

  return (
    <PageShell
      siteName={SITE_CONFIG.name}
      brandSuffix={BRAND_SUFFIX}
      navigationLabel={NAVIGATION_LABEL}
      navigation={NAVIGATION_BY_ROUTE[route.kind]}
      activeSectionId={activeSectionId}
      navigationExpanded={menu.expanded}
      onNavigationToggle={menu.toggle}
      navigationToggleLabel={NAVIGATION_TOGGLE_LABEL}
      navigationToggleRef={menu.toggleRef}
      onNavigate={menu.close}
      themeToggle={<ThemeToggle resolved={resolved} onToggle={toggle} />}
      skipLinkLabel={SKIP_LINK_LABEL}
      footerDomain={FOOTER_DOMAIN}
      footerNote={FOOTER_NOTE}
      footerCredit={FOOTER_CREDIT}
    >
      <RouteView route={route} theme={resolved} />
    </PageShell>
  )
}
